import React, { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { IconHeart } from './components/ui/Icons'

export default function Overlay() {
  const { streamKey } = useParams()
  const [searchParams] = useSearchParams()
  const bgType = searchParams.get('bg') || 'transparent' // 'transparent' | 'green' | 'dark'

  const [data, setData] = useState({
    hr: null,
    rmssd: null,
    zone: null,
    status: 'connecting',
    user: '',
  })

  useEffect(() => {
    if (!streamKey) return
    const API_BASE = import.meta.env.VITE_API_URL || '/api'
    const evSource = new EventSource(`${API_BASE}/stream/${streamKey}/sse`)

    evSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data)
        setData(prev => ({
          ...prev,
          hr: payload.hr ?? null,
          rmssd: payload.rmssd ?? null,
          zone: payload.zone ?? null,
          status: payload.hr ? 'online' : (payload.status ?? 'idle'),
          user: payload.user || prev.user,
        }))
      } catch (e) {
        console.error('Error parsing SSE in overlay:', e)
      }
    }

    evSource.onerror = () => {
      setData(prev => ({ ...prev, status: 'offline' }))
    }

    return () => {
      evSource.close()
    }
  }, [streamKey])

  const bgStyle = {
    transparent: 'transparent',
    green: '#00ff00',
    dark: '#0a0a0f',
  }[bgType] || 'transparent'

  const bpm = data.hr || '--'
  const isOnline = data.status === 'online' && data.hr !== null
  const beatSec = isOnline && data.hr ? (60 / data.hr).toFixed(2) : '1.0'

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: bgStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: 24,
      boxSizing: 'border-box',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#fff',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes heartbeat {
          0% { transform: scale(1); }
          14% { transform: scale(1.18); }
          28% { transform: scale(1); }
          42% { transform: scale(1.1); }
          70% { transform: scale(1); }
        }
      `}</style>

      {/* Modern Stream Widget Card */}
      <div style={{
        background: 'rgba(15, 17, 23, 0.88)',
        backdropFilter: 'blur(16px)',
        border: '1.5px solid rgba(255, 255, 255, 0.12)',
        borderRadius: 20,
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
        minWidth: 210,
      }}>
        {/* Heart Icon with pulse animation */}
        <div style={{
          animation: isOnline ? `heartbeat ${beatSec}s infinite ease-in-out` : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: isOnline ? 'drop-shadow(0 0 10px rgba(255, 59, 48, 0.6))' : 'grayscale(100%) opacity(40%)',
        }}>
          <IconHeart size={36} color="#ff3b30" fill="#ff3b30" />
        </div>

        {/* Heart Rate display */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{
              fontSize: 42,
              fontWeight: 900,
              letterSpacing: -1.5,
              color: isOnline ? '#ffffff' : '#8e8e93',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {bpm}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#8e8e93', letterSpacing: 0.5 }}>
              BPM
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <span style={{
              fontSize: 10,
              fontWeight: 800,
              padding: '2px 7px',
              borderRadius: 6,
              background: isOnline ? 'rgba(52, 199, 89, 0.16)' : 'rgba(142, 142, 147, 0.16)',
              color: isOnline ? '#30d158' : '#8e8e93',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}>
              {isOnline ? (data.zone?.label || 'EN VIVO') : 'DESCONECTADO'}
            </span>

            {isOnline && data.rmssd !== null && (
              <span style={{ fontSize: 11, color: '#a1a1aa', fontWeight: 600 }}>
                HRV {Math.round(data.rmssd)} ms
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
